/**
 * Snapping — zoom-invariant, exactly as the exp editor does it: the tolerance
 * is a fixed number of *screen* pixels converted into millimetres through the
 * current view scale, so it feels the same at 2% and at 800% zoom
 * (spec 2026-08-22-02 §8).
 */
import { clamp, dist, projectToSegment, type Point } from './geometry'
import { holeVertexIndex, spaceHoles, type SceneDoc, type WallElement } from './scene'

/** Screen-pixel tolerances. */
export const SNAP_TOLERANCE_PX = 10
export const WALL_PICK_TOLERANCE_PX = 14
/** Openings are placed by hovering a wall; the pick radius is deliberately
 *  generous so hovering the middle of a doorway gap still finds the wall. */
export const OPENING_PICK_TOLERANCE_PX = 40

export interface SnapCandidate {
    key: string
    /** Vertex index, for polygon rings and wall ends. */
    vi: number
    p: Point
}

export interface SnapExclusion {
    key: string
    vi?: number
}

/** Every snappable model point: wall endpoints, room vertices, equipment anchors, run bends, risers. */
export function snapCandidates(
    doc: SceneDoc,
    exclude?: SnapExclusion,
    excludeRegistrationUid?: string,
): SnapCandidate[] {
    const out: SnapCandidate[] = []
    const push = (key: string, vi: number, p: Point) => {
        if (exclude && exclude.key === key && (exclude.vi === undefined || exclude.vi === vi)) return
        out.push({ key, vi, p })
    }
    for (const el of doc.elements) {
        // Calibrating a layer means aiming at the *rest* of the scene: its own
        // vertices are the thing being moved, so they never snap (spec §1).
        if (excludeRegistrationUid && el.source.registrationUid === excludeRegistrationUid) continue
        if (el.kind === 'wall') {
            push(el.key, 0, el.start)
            push(el.key, 1, el.end)
        } else if (el.kind === 'space') {
            el.boundary.forEach((p, i) => push(el.key, i, p))
            // A hole's corners snap like the boundary's, numbered after it
            // (`holeVertexIndex`), so the corner being dragged can exclude
            // itself and nothing else.
            spaceHoles(el).forEach((h, hi) => h.forEach((p, j) => push(el.key, holeVertexIndex(el, hi, j), p)))
        } else if (el.kind === 'equipment') {
            push(el.key, 0, el.position)
        } else if (el.kind === 'run') {
            // A run's bends: a branch drawn from an existing line lands on it.
            el.points.forEach((p, i) => push(el.key, i, p))
        } else if (el.kind === 'riser') {
            // A riser's centre: a branch drawn off the column starts on it.
            push(el.key, 0, el.position)
        }
    }
    return out
}

export interface SnapOptions {
    /** Screen pixels per millimetre. */
    scale: number
    enabled: boolean
    /** Shift: hard axis-lock to the dominant direction from `anchor`. */
    axisLock?: boolean
    anchor?: Point
    exclude?: SnapExclusion
    /** Skips every element of one registration — the layer being calibrated. */
    excludeRegistrationUid?: string
}

export interface SnapResult {
    pt: Point
    snappedTo: Point | null
}

export function snapPoint(raw: Point, doc: SceneDoc, opts: SnapOptions): SnapResult {
    let pt: Point = { ...raw }
    let snappedTo: Point | null = null
    const tol = SNAP_TOLERANCE_PX / Math.max(opts.scale, 1e-9)

    if (opts.enabled) {
        let best: SnapCandidate | null = null
        let bestD = tol
        for (const c of snapCandidates(doc, opts.exclude, opts.excludeRegistrationUid)) {
            const d = dist(raw, c.p)
            if (d < bestD) {
                bestD = d
                best = c
            }
        }
        if (best) {
            pt = { ...best.p }
            snappedTo = best.p
        }
    }

    const anchor = opts.anchor
    if (anchor) {
        if (opts.axisLock) {
            if (Math.abs(pt.x - anchor.x) > Math.abs(pt.y - anchor.y)) pt.y = anchor.y
            else pt.x = anchor.x
        } else if (opts.enabled && !snappedTo) {
            if (Math.abs(pt.x - anchor.x) < tol) pt.x = anchor.x
            if (Math.abs(pt.y - anchor.y) < tol) pt.y = anchor.y
        }
    }
    return { pt, snappedTo }
}

export interface WallHit {
    wall: WallElement
    pt: Point
    t: number
    d: number
}

/** The nearest wall to a point, within a screen-distance tolerance. */
export function nearestWall(
    doc: SceneDoc,
    mm: Point,
    scale: number,
    tolScreenPx = WALL_PICK_TOLERANCE_PX,
): WallHit | null {
    let best: WallHit | null = null
    let bestD = tolScreenPx / Math.max(scale, 1e-9)
    for (const el of doc.elements) {
        if (el.kind !== 'wall') continue
        const pr = projectToSegment(mm, el.start, el.end)
        if (pr.d < bestD) {
            bestD = pr.d
            best = { wall: el, pt: pr.pt, t: pr.t, d: pr.d }
        }
    }
    return best
}

/**
 * Where an opening of `width` would sit on `wall` for a pointer at `mm`.
 * The abscissa is taken on the wall's *unclamped* line and then allowed to
 * overshoot by one opening width, so a door clicked in the gap between two
 * collinear segments centres itself in that gap.
 */
export function openingPlacement(wall: WallElement, mm: Point, width: number): { center: Point; t: number } {
    const dx = wall.end.x - wall.start.x
    const dy = wall.end.y - wall.start.y
    const len2 = dx * dx + dy * dy
    const rawT = len2 ? ((mm.x - wall.start.x) * dx + (mm.y - wall.start.y) * dy) / len2 : 0
    const over = width / Math.max(Math.sqrt(len2), 1)
    const t = clamp(rawT, -over, 1 + over)
    return { center: { x: wall.start.x + dx * t, y: wall.start.y + dy * t }, t }
}

export const wallDirectionDeg = (wall: WallElement): number =>
    (Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) * 180) / Math.PI
